import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  createDisplayAndEventsRequest,
  fetchUserDivision,
  getDisplayAndEventsAreaSalesManagers,
  getDisplayAndEventsHistory,
  getDisplayAndEventsRequests
} from 'Service/ApiService';
import { showAlert } from 'utils/ErrorMessage';

const getTodayDate = () => {
  const today = new Date();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');

  return `${today.getFullYear()}-${month}-${day}`;
};

const resolveAsmUserCode = (user = {}) => (
  user?.asmUserCode
  || user?.asmCode
  || user?.asm_code
  || user?.reportingManagerCode
  || user?.managerCode
  || user?.salesOfficerCode
  || ''
);

export const getInitialDisplayAndEventsForm = (user = {}) => ({
  userCode: user?.userCode || '',
  asmUserCode: resolveAsmUserCode(user),
  eventname: '',
  registersrno: '',
  division: '',
  requestdate: getTodayDate(),
  startdate: '',
  enddate: '',
  eventtype: '',
  participatingareas: '',
  expectedsalesqty: '',
  expectedsalesamount: '',
  expecteddealersno: '',
  expectedretailersno: '',
  customeravgdaystay: '',
  expectedagentsno: '',
  staffavgdaystay: '',
  expectedstaffno: '',
  hotelcostperperson: '',
  hoteltotalcost: '',
  travelcostperperson: '',
  traveltotalcost: '',
  displaycost: '',
  logisticscost: '',
  photovideoshootcost: '',
  totalestimatedcost: '',
  expenditurepercentage: '',
  expenditurepercustomer: ''
});

export const initialDisplayAndEventsFilters = {
  eventname: '',
  division: '',
  fromDate: '',
  toDate: '',
  status: ''
};

const normalizeEventRequest = (request = {}) => ({
  showid: request.showid || '',
  eventid: request.eventid || '',
  eventname: request.eventname || '',
  registersrno: request.registersrno || '',
  division: request.division || '',
  requestdate: request.requestdate || '',
  startdate: request.startdate || '',
  enddate: request.enddate || '',
  eventtype: request.eventtype || '',
  participatingareas: request.participatingareas || '',
  expectedsalesqty: request.expectedsalesqty || '',
  expectedsalesamount: request.expectedsalesamount || '',
  expecteddealersno: request.expecteddealersno || '',
  expectedretailersno: request.expectedretailersno || '',
  customeravgdaystay: request.customeravgdaystay || '',
  expectedagentsno: request.expectedagentsno || '',
  staffavgdaystay: request.staffavgdaystay || '',
  expectedstaffno: request.expectedstaffno || '',
  hotelcostperperson: request.hotelcostperperson || '',
  hoteltotalcost: request.hoteltotalcost || '',
  travelcostperperson: request.travelcostperperson || '',
  traveltotalcost: request.traveltotalcost || '',
  displaycost: request.displaycost || '',
  logisticscost: request.logisticscost || '',
  photovideoshootcost: request.photovideoshootcost || '',
  totalestimatedcost: request.totalestimatedcost || '',
  expenditurepercentage: request.expenditurepercentage || '',
  expenditurepercustomer: request.expenditurepercustomer || '',
  status: request.status || request.lastApprovalStatus || '',
  requestsubmittedby: request.requestsubmittedby || '',
  asmUserCode: request.asmUserCode || '',
  lastAction: request.lastAction || '',
  lastActionByRole: request.lastActionByRole || '',
  lastActionDate: request.lastActionDate || '',
  lastApprovalStatus: request.lastApprovalStatus || '',
  createdat: request.createdat || '',
  updatedat: request.updatedat || ''
});

const normalizeActionHistory = (item = {}) => ({
  id: item.approvalid || `${item.eventid || ''}-${item.actiondate || item.createdat || ''}`,
  action: item.actiontype || '',
  actionBy: item.actionByUserCode || '',
  role: item.actionByRole || '',
  status: item.status || '',
  remarks: item.remarks || '',
  actionDate: item.actiondate || item.createdat || ''
});

const buildUserFullName = (user) => [user?.firstName, user?.lastName]
  .filter((value) => String(value || '').trim())
  .join(' ')
  .trim();

const buildAsmUserOption = (user) => {
  const userCode = String(user?.userCode || '').trim();
  const fullName = buildUserFullName(user);

  return {
    value: userCode,
    label: [fullName, userCode].filter(Boolean).join(' - ')
  };
};

const buildDivisionOption = (division = {}) => {
  const value = String(division?.value || '').trim();
  const label = String(division?.label || '').trim();

  return {
    value,
    label: label && value ? `${label} (${value})` : label || value
  };
};

const DispayAndEventsController = () => {
  const loggedInUser = useMemo(() => JSON.parse(localStorage.getItem('user') || '{}'), []);
  const loggedInUserCode = String(loggedInUser?.userCode || '').trim();
  const [asmUserOptions, setAsmUserOptions] = useState([]);
  const [asmUserSearchText, setAsmUserSearchText] = useState('');
  const [divisionOptions, setDivisionOptions] = useState([]);
  const [eventRequests, setEventRequests] = useState([]);
  const [filters, setFilters] = useState(initialDisplayAndEventsFilters);
  const [totalRows, setTotalRows] = useState(0);
  const [actionHistory, setActionHistory] = useState([]);
  const [historyError, setHistoryError] = useState('');
  const [isAsmUserOptionsLoading, setIsAsmUserOptionsLoading] = useState(false);
  const [isDivisionOptionsLoading, setIsDivisionOptionsLoading] = useState(false);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let isMounted = true;

    const loadDivisionOptions = async () => {
      setIsDivisionOptionsLoading(true);

      try {
        const response = await fetchUserDivision(loggedInUserCode);

        if (!isMounted) {
          return;
        }

        if (!response || (response.success !== true && response.success !== 'true')) {
          throw new Error(response?.msg || 'Failed to load divisions.');
        }

        const nextOptions = Array.isArray(response?.data)
          ? response.data.map(buildDivisionOption).filter((option) => option.value)
          : [];

        setDivisionOptions(nextOptions);
      } catch (error) {
        if (isMounted) {
          setDivisionOptions([]);
          showAlert(error?.message || 'Failed to load divisions.');
        }
      } finally {
        if (isMounted) {
          setIsDivisionOptionsLoading(false);
        }
      }
    };

    loadDivisionOptions();

    return () => {
      isMounted = false;
    };
  }, [loggedInUserCode]);

  useEffect(() => {
    let isMounted = true;
    const trimmedSearchText = asmUserSearchText.trim();

    const loadAsmUserOptions = async () => {
      if (trimmedSearchText && trimmedSearchText.length < 3) {
        if (isMounted) {
          setIsAsmUserOptionsLoading(false);
        }
        return;
      }

      setIsAsmUserOptionsLoading(true);

      try {
        const response = await getDisplayAndEventsAreaSalesManagers(trimmedSearchText, loggedInUserCode);

        if (!isMounted) {
          return;
        }

        if (!response || (response.success !== true && response.success !== 'true')) {
          throw new Error(response?.msg || 'Failed to load ASM users.');
        }

        const nextOptions = Array.isArray(response?.userLevelList)
          ? response.userLevelList.map(buildAsmUserOption).filter((option) => option.value)
          : [];

        setAsmUserOptions(nextOptions);
      } catch (error) {
        if (isMounted) {
          setAsmUserOptions([]);
          showAlert(error?.message || 'Failed to load ASM users.');
        }
      } finally {
        if (isMounted) {
          setIsAsmUserOptionsLoading(false);
        }
      }
    };

    const debounceTimer = setTimeout(loadAsmUserOptions, 300);

    return () => {
      isMounted = false;
      clearTimeout(debounceTimer);
    };
  }, [asmUserSearchText, loggedInUserCode]);

  const loadEventRequests = useCallback(async () => {
    setIsTableLoading(true);

    try {
      const response = await getDisplayAndEventsRequests(loggedInUserCode, filters);

      if (!response || (response.success !== true && response.success !== 'true')) {
        throw new Error(response?.msg || 'Failed to load display and events requests.');
      }

      const nextRequests = Array.isArray(response?.data)
        ? response.data.map(normalizeEventRequest)
        : [];

      setEventRequests(nextRequests);
      setTotalRows(Number(response?.totalRows || nextRequests.length || 0));
    } catch (error) {
      setEventRequests([]);
      setTotalRows(0);
      showAlert(error?.message || 'Failed to load display and events requests.');
    } finally {
      setIsTableLoading(false);
    }
  }, [filters, loggedInUserCode]);

  useEffect(() => {
    const debounceTimer = setTimeout(loadEventRequests, 300);

    return () => {
      clearTimeout(debounceTimer);
    };
  }, [loadEventRequests, refreshKey]);

  const submitEventRequest = async (formValues) => {
    setIsSubmitting(true);

    try {
      const response = await createDisplayAndEventsRequest(formValues);

      if (!response || (response.success !== true && response.success !== 'true')) {
        throw new Error(response?.msg || 'Failed to submit event request.');
      }

      setRefreshKey((prev) => prev + 1);
      return response;
    } finally {
      setIsSubmitting(false);
    }
  };

  const loadActionHistory = async (eventId) => {
    if (!eventId) {
      setActionHistory([]);
      setHistoryError('Event id is missing.');
      return;
    }

    setIsHistoryLoading(true);
    setHistoryError('');

    try {
      const response = await getDisplayAndEventsHistory(eventId);

      if (!response || (response.success !== true && response.success !== 'true')) {
        throw new Error(response?.msg || 'Failed to load history.');
      }

      const nextHistory = Array.isArray(response?.data)
        ? response.data.map(normalizeActionHistory)
        : [];

      setActionHistory(nextHistory);
    } catch (error) {
      setActionHistory([]);
      setHistoryError(error?.message || 'Failed to load history.');
    } finally {
      setIsHistoryLoading(false);
    }
  };

  const clearActionHistory = () => {
    setActionHistory([]);
    setHistoryError('');
    setIsHistoryLoading(false);
  };

  return {
    actionHistory,
    asmUserOptions,
    clearActionHistory,
    divisionOptions,
    eventRequests,
    filters,
    historyError,
    isAsmUserOptionsLoading,
    isDivisionOptionsLoading,
    isHistoryLoading,
    isSubmitting,
    isTableLoading,
    loadActionHistory,
    loggedInUser,
    loggedInUserCode,
    refreshList: loadEventRequests,
    setAsmUserSearchText,
    setFilters,
    submitEventRequest,
    totalRows
  };
};

export default DispayAndEventsController;
